import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Container,
  Divider,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  CheckCircle,
  Pending,
  Cancel,
  Inventory,
  TrendingDown,
  Receipt,
  Visibility,
  Edit,
  PlaylistAddCheck,
  LocalShipping,
  Delete,
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

export const InvSupplierReturns = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnDialog, setReturnDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});

  const [newReturn, setNewReturn] = useState({
    items: [
      {
        item: "",
        batchNumber: "",
        quantity: 1,
        unitCost: 0,
        reason: "",
        condition: "damaged",
      },
    ],
    returnDate: new Date().toISOString().split("T")[0],
    reason: "",
    notes: "",
  });

  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    notes: "",
    creditNoteNumber: "",
  });

  useEffect(() => {
    fetchReturns();
    fetchReturnStatistics();
    fetchSupplierProducts();
  }, [id]);

  const fetchReturns = async () => {
    try {
      const response = await suppliersAPI.getReturns(id);
      setReturns(response.data.data.returns || []);
    } catch (error) {
      enqueueSnackbar("Error fetching returns", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnStatistics = async () => {
    try {
      const response = await suppliersAPI.getReturnStatistics(id);
      setStatistics(response.data.data);
    } catch (error) {
      console.error("Error fetching return statistics:", error);
    }
  };

  const fetchSupplierProducts = async () => {
    try {
      const response = await suppliersAPI.getItems(id);
      setSupplierProducts(response.data.data.items || []);
    } catch (error) {
      console.error("Error fetching supplier products:", error);
      enqueueSnackbar("Error fetching supplier products", { variant: "error" });
    }
  };

  const fetchBatchesForProduct = async (itemId, itemIndex) => {
    try {
      const response = await suppliersAPI.searchBatchesForReturn(id, {
        itemId,
      });

      setAvailableBatches((prev) => ({
        ...prev,
        [itemIndex]: response.data.data?.batches || [],
      }));
    } catch (error) {
      console.error("Error fetching batches:", error);
      enqueueSnackbar("Error fetching batches for selected product", {
        variant: "error",
      });
    }
  };

  const handleCreateReturn = async () => {
    try {
      await suppliersAPI.createReturn(id, newReturn);
      enqueueSnackbar("Return created successfully", { variant: "success" });
      setReturnDialog(false);
      setNewReturn({
        items: [
          {
            item: "",
            batchNumber: "",
            quantity: 1,
            unitCost: 0,
            reason: "",
            condition: "damaged",
          },
        ],
        returnDate: new Date().toISOString().split("T")[0],
        reason: "",
        notes: "",
        purchaseOrder: "",
      });
      setAvailableBatches({});
      fetchReturns();
      fetchReturnStatistics();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error creating return",
        { variant: "error" }
      );
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await suppliersAPI.updateReturnStatus(
        id,
        selectedReturn._id,
        statusUpdate
      );
      enqueueSnackbar(`Return ${statusUpdate.status} successfully`, {
        variant: "success",
      });
      setStatusDialog(false);
      setSelectedReturn(null);
      setStatusUpdate({ status: "", notes: "", creditNoteNumber: "" });
      fetchReturns();
      fetchReturnStatistics();
    } catch (error) {
      enqueueSnackbar("Error updating return status", { variant: "error" });
    }
  };

  const addReturnItem = () => {
    setNewReturn((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: "",
          batchNumber: "",
          quantity: 1,
          unitCost: 0,
          reason: "",
          condition: "damaged",
        },
      ],
    }));
  };

  const updateReturnItem = (index, field, value) => {
    setNewReturn((prev) => {
      const updatedItems = [...prev.items];
      const updatedItem = { ...updatedItems[index], [field]: value };

      if (field === "item") {
        updatedItem.batchNumber = "";
        updatedItem.unitCost = 0;
      }

      updatedItems[index] = updatedItem;

      return {
        ...prev,
        items: updatedItems,
      };
    });

    if (field === "item" && value) {
      fetchBatchesForProduct(value, index);
    }
  };

  const removeReturnItem = (index) => {
    setNewReturn((prev) => {
      if (prev.items.length <= 1) return prev;
      const updatedItems = prev.items.filter((_, i) => i !== index);
      return { ...prev, items: updatedItems };
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      pending: "warning",
      approved: "info",
      completed: "success",
      rejected: "error",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <Pending />,
      pending: <Pending />,
      approved: <PlaylistAddCheck />,
      completed: <CheckCircle />,
      rejected: <Cancel />,
      cancelled: <Cancel />,
    };
    return icons[status] || <Pending />;
  };

  const handleViewReturn = (returnRecord) => {
    setSelectedReturn(returnRecord);
    setViewDialog(true);
  };

  const handleStatusUpdate = (returnRecord) => {
    setSelectedReturn(returnRecord);
    setStatusUpdate({ status: "", notes: "", creditNoteNumber: "" });
    setStatusDialog(true);
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/hospital/hospital-inventory/suppliers")}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Back to Suppliers
          </Button>
          <Box>
            <Typography variant="h3" fontWeight="bold" color="primary.main" gutterBottom>
              Supplier Returns
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage and track supplier returns efficiently
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setReturnDialog(true)}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            fontSize: '1rem'
          }}
          size="large"
        >
          Create Return
        </Button>
      </Box>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: <Inventory sx={{ fontSize: 32 }} />,
              value: statistics.statistics?.totalReturns || 0,
              label: "Total Returns",
              color: "primary.main"
            },
            {
              icon: <Receipt sx={{ fontSize: 32 }} />,
              value: statistics.statistics?.pendingReturns || 0,
              label: "Pending Returns",
              color: "warning.main"
            },
            {
              icon: <TrendingDown sx={{ fontSize: 32 }} />,
              value: `₹${statistics.statistics?.totalCreditAmount?.toLocaleString() || 0}`,
              label: "Total Credit",
              color: "success.main"
            },
            {
              icon: <CheckCircle sx={{ fontSize: 32 }} />,
              value: statistics.statistics?.completedReturns || 0,
              label: "Completed",
              color: "info.main"
            },
            {
              icon: <LocalShipping sx={{ fontSize: 32 }} />,
              value: `₹${statistics.statistics?.averageReturnValue?.toLocaleString() || 0}`,
              label: "Avg Return Value",
              color: "secondary.main"
            }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: stat.color, mb: 2 }}>
                    {stat.icon}
                  </Box>
                  <Typography 
                    variant="h3" 
                    fontWeight="bold" 
                    color={stat.color}
                    sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ fontSize: '1rem', mt: 1 }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          boxShadow: 2
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 600,
                py: 2,
                px: 3,
                minWidth: 'auto'
              }
            }}
          >
            <Tab label="All Returns" />
            <Tab label="Pending" />
            <Tab label="Approved" />
            <Tab label="Completed" />
          </Tabs>
        </CardContent>
      </Card>

      {/* Table */}
      <ReturnTable
        returns={returns}
        tabValue={tabValue}
        onView={handleViewReturn}
        onStatusUpdate={handleStatusUpdate}
        getStatusColor={getStatusColor}
        getStatusIcon={getStatusIcon}
      />

      {/* Create Return Dialog */}
      <Dialog
        open={returnDialog}
        onClose={() => setReturnDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          py: 3
        }}>
          Create Supplier Return
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {supplierProducts.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  No products found for this supplier. Please add products to this supplier first.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom fontWeight="bold" color="primary.main">
                Return Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Return Date"
                    value={newReturn.returnDate}
                    onChange={(e) =>
                      setNewReturn((prev) => ({
                        ...prev,
                        returnDate: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    value={newReturn.reason}
                    onChange={(e) =>
                      setNewReturn((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    placeholder="Overall reason for return..."
                    required
                    sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    value={newReturn.notes}
                    onChange={(e) =>
                      setNewReturn((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Additional notes about this return..."
                    sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  Return Items
                </Typography>
                <Button 
                  onClick={addReturnItem} 
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Add Item
                </Button>
              </Box>

              {newReturn.items.map((item, index) => (
                <Card
                  key={index}
                  sx={{ 
                    mb: 3, 
                    p: 3, 
                    bgcolor: 'background.default',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Grid container spacing={3} alignItems="center">
                    {/* Product */}
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required>
                        <InputLabel sx={{ fontSize: '1rem' }}>Product</InputLabel>
                        <Select
                          value={item.item}
                          onChange={(e) =>
                            updateReturnItem(index, "item", e.target.value)
                          }
                          label="Product"
                          sx={{ '& .MuiSelect-select': { fontSize: '1rem' } }}
                        >
                          <MenuItem value="" disabled>
                            Select Product
                          </MenuItem>
                          {supplierProducts.map((supplierItem) => (
                            <MenuItem
                              key={supplierItem._id}
                              value={supplierItem.item?._id}
                              sx={{ fontSize: '1rem' }}
                            >
                              {supplierItem.item?.name} - {supplierItem.item?.brand}
                              {supplierItem.item?.category && ` (${supplierItem.item.category})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Batch Number */}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth required>
                        <InputLabel sx={{ fontSize: '1rem' }}>Batch Number</InputLabel>
                        <Select
                          value={item.batchNumber}
                          onChange={(e) => {
                            const batchNumber = e.target.value;
                            const selectedBatch = availableBatches[index]?.find(
                              (b) => b.batchNumber === batchNumber
                            );

                            updateReturnItem(index, "batchNumber", batchNumber);

                            if (selectedBatch) {
                              updateReturnItem(
                                index,
                                "unitCost",
                                selectedBatch.costPrice
                              );
                            }
                          }}
                          label="Batch Number"
                          disabled={!item.item}
                          sx={{ '& .MuiSelect-select': { fontSize: '1rem' } }}
                        >
                          <MenuItem value="" disabled sx={{ fontSize: '1rem' }}>
                            {item.item ? "Select Batch" : "Select Product First"}
                          </MenuItem>
                          {availableBatches[index]?.map((batch) => (
                            <MenuItem
                              key={batch.batchId}
                              value={batch.batchNumber}
                              sx={{ fontSize: '1rem' }}
                            >
                              {batch.batchNumber} - Qty: {batch.availableQuantity} - ₹{batch.costPrice}
                              {batch.expiryDate && ` (Exp: ${new Date(batch.expiryDate).toLocaleDateString()})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Quantity */}
                    <Grid item xs={12} md={1}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Qty"
                        value={item.quantity}
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? ""
                              : parseInt(e.target.value, 10);
                          if (e.target.value === "") {
                            updateReturnItem(index, "quantity", "");
                          } else if (!isNaN(value) && value >= 0) {
                            updateReturnItem(index, "quantity", value);
                          }
                        }}
                        onBlur={(e) => {
                          if (
                            e.target.value === "" ||
                            parseInt(e.target.value, 10) < 1
                          ) {
                            updateReturnItem(index, "quantity", 1);
                          }
                        }}
                        inputProps={{
                          min: 1,
                          max:
                            availableBatches[index]?.find(
                              (b) => b.batchNumber === item.batchNumber
                            )?.availableQuantity || 999,
                        }}
                        helperText={
                          item.batchNumber &&
                          availableBatches[index]?.find(
                            (b) => b.batchNumber === item.batchNumber
                          )
                            ? `Max: ${
                                availableBatches[index].find(
                                  (b) => b.batchNumber === item.batchNumber
                                ).availableQuantity
                              }`
                            : ""
                        }
                        required
                      />
                    </Grid>

                    {/* Unit Cost */}
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Unit Cost"
                        value={item.unitCost === 0 ? "" : item.unitCost}
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value);
                          updateReturnItem(index, "unitCost", value);
                        }}
                        onBlur={(e) => {
                          if (
                            e.target.value === "" ||
                            parseFloat(e.target.value) < 0
                          ) {
                            updateReturnItem(index, "unitCost", 0);
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>₹</Typography>
                          ),
                        }}
                        required
                      />
                    </Grid>

                    {/* Condition */}
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        select
                        label="Condition"
                        value={item.condition}
                        onChange={(e) =>
                          updateReturnItem(index, "condition", e.target.value)
                        }
                        required
                        sx={{ '& .MuiSelect-select': { fontSize: '1rem' } }}
                      >
                        <MenuItem value="damaged" sx={{ fontSize: '1rem' }}>Damaged</MenuItem>
                        <MenuItem value="expired" sx={{ fontSize: '1rem' }}>Expired</MenuItem>
                        <MenuItem value="wrong_item" sx={{ fontSize: '1rem' }}>Wrong Item</MenuItem>
                        <MenuItem value="quality_issue" sx={{ fontSize: '1rem' }}>Quality Issue</MenuItem>
                        <MenuItem value="excess_stock" sx={{ fontSize: '1rem' }}>Excess Stock</MenuItem>
                        <MenuItem value="other" sx={{ fontSize: '1rem' }}>Other</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Reason */}
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Reason"
                        value={item.reason}
                        onChange={(e) =>
                          updateReturnItem(index, "reason", e.target.value)
                        }
                        placeholder="Item reason"
                        required
                        sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                      />
                    </Grid>

                    {/* Remove button */}
                    {newReturn.items.length > 1 && (
                      <Grid item xs={12} md={1}>
                        <Tooltip title="Remove Item">
                          <IconButton
                            color="error"
                            onClick={() => removeReturnItem(index)}
                            size="large"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setReturnDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2, fontSize: '1rem', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateReturn}
            variant="contained"
            disabled={
              supplierProducts.length === 0 ||
              !newReturn.reason ||
              newReturn.items.some((item) => !item.item || !item.batchNumber)
            }
            sx={{ borderRadius: 2, fontSize: '1rem', px: 3 }}
          >
            Create Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialog}
        onClose={() => setStatusDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          py: 3
        }}>
          Update Return Status
          {selectedReturn && (
            <Typography variant="body1" sx={{ color: 'white', mt: 1 }}>
              {selectedReturn.returnNumber}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedReturn && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1rem' }}>New Status</InputLabel>
                  <Select
                    value={statusUpdate.status}
                    onChange={(e) =>
                      setStatusUpdate((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    label="New Status"
                    sx={{ '& .MuiSelect-select': { fontSize: '1rem' } }}
                  >
                    <MenuItem value="pending" sx={{ fontSize: '1rem' }}>Pending</MenuItem>
                    <MenuItem value="approved" sx={{ fontSize: '1rem' }}>Approved</MenuItem>
                    <MenuItem value="completed" sx={{ fontSize: '1rem' }}>Completed</MenuItem>
                    <MenuItem value="rejected" sx={{ fontSize: '1rem' }}>Rejected</MenuItem>
                    <MenuItem value="cancelled" sx={{ fontSize: '1rem' }}>Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {statusUpdate.status === "completed" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Credit Note Number"
                    value={statusUpdate.creditNoteNumber}
                    onChange={(e) =>
                      setStatusUpdate((prev) => ({
                        ...prev,
                        creditNoteNumber: e.target.value,
                      }))
                    }
                    placeholder="CN-XXXXXX"
                    sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Status Notes"
                  value={statusUpdate.notes}
                  onChange={(e) =>
                    setStatusUpdate((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Reason for status change..."
                  sx={{ '& .MuiInputBase-input': { fontSize: '1rem' } }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setStatusDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2, fontSize: '1rem', px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={!statusUpdate.status}
            sx={{ borderRadius: 2, fontSize: '1rem', px: 3 }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Return Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          py: 3
        }}>
          Return Details - {selectedReturn?.returnNumber}
          <Chip
            label={selectedReturn?.status}
            color={getStatusColor(selectedReturn?.status)}
            sx={{ 
              ml: 2,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedReturn && (
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Return Number
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {selectedReturn.returnNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Return Date
                </Typography>
                <Typography variant="h6">
                  {new Date(selectedReturn.returnDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Credit
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  ₹{selectedReturn.totalCreditAmount?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Reason
                </Typography>
                <Typography variant="h6">{selectedReturn.reason}</Typography>
              </Grid>

              {/* Items table */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary.main">
                  Return Items
                </Typography>
                <TableContainer 
                  component={Paper} 
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Product</TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Batch Number</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Quantity</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Unit Cost</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Total Credit</TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Condition</TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1rem', fontWeight: 'bold' }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedReturn.items?.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.item?.name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {item.batchNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" fontSize="1rem">
                              {item.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" fontSize="1rem">
                              ₹{item.unitCost?.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              ₹{item.totalCredit?.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.condition?.replace(/_/g, " ")}
                              color="primary"
                              sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1">
                              {item.reason}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {selectedReturn.notes && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                    {selectedReturn.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setViewDialog(false)}
            variant="contained"
            sx={{ borderRadius: 2, fontSize: '1rem', px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

const ReturnTable = ({
  returns,
  tabValue,
  onView,
  onStatusUpdate,
  getStatusColor,
  getStatusIcon,
}) => {
  const filteredReturns = returns.filter((returnRecord) => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return returnRecord.status === "pending";
    if (tabValue === 2) return returnRecord.status === "approved";
    if (tabValue === 3) return returnRecord.status === "completed";
    return true;
  });

  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: 3
    }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary.main">
            {tabValue === 0
              ? "All"
              : tabValue === 1
              ? "Pending"
              : tabValue === 2
              ? "Approved"
              : "Completed"}{" "}
            Returns ({filteredReturns.length})
          </Typography>
        </Box>

        {filteredReturns.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: '1rem' }}>
              No return records found.
            </Alert>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Return Number</TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Reason</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Total Credit</TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Credit Note</TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReturns.map((returnRecord) => (
                  <TableRow 
                    key={returnRecord._id} 
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {returnRecord.returnNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontSize="1rem">
                        {new Date(returnRecord.returnDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" noWrap sx={{ maxWidth: 200, fontSize: '1rem' }}>
                        {returnRecord.reason}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        ₹{returnRecord.totalCreditAmount?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(returnRecord.status)}
                        label={returnRecord.status.toUpperCase()}
                        color={getStatusColor(returnRecord.status)}
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          px: 1
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {returnRecord.creditNoteNumber ? (
                        <Chip
                          label={returnRecord.creditNoteNumber}
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                        />
                      ) : (
                        <Typography variant="body1" color="text.secondary" fontSize="1rem">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="large"
                            onClick={() => onView(returnRecord)}
                            color="primary"
                            sx={{ 
                              bgcolor: 'primary.light',
                              '&:hover': { bgcolor: 'primary.main' }
                            }}
                          >
                            <Visibility sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Status">
                          <IconButton
                            size="large"
                            onClick={() => onStatusUpdate(returnRecord)}
                            color="warning"
                            sx={{ 
                              bgcolor: 'warning.light',
                              '&:hover': { bgcolor: 'warning.main' }
                            }}
                          >
                            <Edit sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default InvSupplierReturns;